import React, { useState } from 'react';
import { Typography, message } from 'antd';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useWishlist } from '../context/WishlistContext';
import { useProductOffers } from '../hooks/useProductOffers';
import { offerText } from '../utils/offers';
import { ProductVariant } from '../types/product';
import '../styles/VegetableCard.css';
import { imageUrl } from '../utils/imageUrl';

const { Text } = Typography;

interface VegetableCardProps {
  id: number;
  name: string;
  nameHi: string;
  price: number;
  mrp?: number;
  unit: string;
  emoji: string;
  farm: string;
  stock: number;
  maxOrderQty?: number;
  desc?: string;
  benefits?: string[];
  storage?: string;
  category?: string;
  seasonal?: boolean;
  primaryImage?: string;
  images?: { id: number; mediaType: 'image' | 'video'; mediaUrl: string; sortOrder: number; isPrimary: boolean }[];
  variants?: ProductVariant[];
  minVariantPrice?: number;
  /** Announced, but not on sale yet. Shows instead of the ADD button. */
  comingSoon?: boolean;
}

const VegetableCard: React.FC<VegetableCardProps> = ({
  id, name, nameHi, price, mrp, unit, emoji, farm, stock,
  maxOrderQty, seasonal, primaryImage, images, variants, comingSoon,
}) => {
  const [imgIdx, setImgIdx] = useState(0);
  const { addToCart, variantLimit, removeFromCart, updateQty, items } = useCart();
  const { isAuthenticated } = useCustomerAuth();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const offers = useProductOffers();
  const offer = offers.get(id);
  const [messageApi, contextHolder] = message.useMessage();

  const cartItem = items.find(i => i.id === id && !i.variantId);
  const inCart = cartItem?.qty ?? 0;
  const outOfStock = stock === 0;
  // Checked before the stock overlay, and separately from it. A coming-soon
  // product may well have stock in the warehouse, and "Out of Stock" is the
  // wrong promise either way: it says we had this and ran out, where the whole
  // point of announcing something is that nobody has had it yet.
  const comingSoonOnly = !!comingSoon;
  // Budget against other variants of this product already in the cart
  const effectiveMax = variantLimit(id, 0, stock, maxOrderQty, 0);
  const capReached = maxOrderQty != null && inCart >= effectiveMax;

  const imgUrls = (images ?? [])
    .filter(i => i.mediaType === 'image')
    .sort((a, b) => (a.isPrimary ? -1 : b.isPrimary ? 1 : a.sortOrder - b.sortOrder))
    // 400 is the padded square derivative: the card is 1:1 and a raw upload
    // would be centre-cropped by object-fit.
    .map(i => imageUrl(i.mediaUrl, 400)!);
  if (imgUrls.length === 0 && primaryImage) imgUrls.push(imageUrl(primaryImage, 400)!);
  const currentImg = imgUrls[imgIdx] ?? null;

  const showMrp = mrp != null && mrp > price;
  // Drop variants whose single unit already exceeds the per-order cap — they're unbuyable
  // (e.g. a 5 kg pack under a 4 kg/order limit), so they shouldn't be advertised on the card.
  const activeVariants = variants?.filter(
    v => v.isActive && (maxOrderQty == null || v.weightGrams / 1000 <= maxOrderQty),
  ) ?? [];
  const hasVariants = activeVariants.length > 0;

  const handleAdd = () => {
    const added = addToCart({ id, name, price, unit, emoji, image: currentImg ?? undefined, farm, stock, maxQty: maxOrderQty });
    if (!added) {
      if (capReached) {
        messageApi.warning(`Max ${maxOrderQty} ${unit} of ${name} per order (across all sizes)`);
      } else {
        messageApi.warning(`Only ${stock} ${unit} available`);
      }
    }
  };

  const handleIncrement = () => {
    if (inCart >= effectiveMax) {
      messageApi.warning(
        capReached
          ? `Max ${maxOrderQty} ${unit} of ${name} per order (across all sizes)`
          : `Only ${stock} ${unit} available`
      );
      return;
    }
    updateQty(id, inCart + 1);
  };

  const handleDecrement = () => {
    if (inCart <= 1) removeFromCart(id);
    else updateQty(id, inCart - 1);
  };

  return (
    <div className="vcard">
      {contextHolder}

      <div className="vcard-media">
        {currentImg
          ? <img src={currentImg} alt={name} className="vcard-img" loading="lazy" decoding="async" />
          : <div className="vcard-emoji-fallback">{emoji}</div>
        }

        {isAuthenticated && (
          <button
            className={`vcard-heart${isWishlisted(id) ? ' vcard-heart--liked' : ''}`}
            onClick={() => toggleWishlist(id)}
            aria-label="Wishlist"
          >
            {isWishlisted(id) ? '❤️' : '🤍'}
          </button>
        )}

        {/* A status badge, the way every other shelf in the category does it —
            the product photo stays visible and in colour. An announced product
            is something to want, not a failure to apologise for, so it is not
            greyed out or covered over. */}
        {comingSoonOnly && <span className="vcard-soon-tag">Coming Soon</span>}

        {seasonal && (
          <span className={`vcard-seasonal-tag${comingSoonOnly ? ' vcard-seasonal-tag--stacked' : ''}`}>
            🍂 Seasonal
          </span>
        )}

        {outOfStock && !comingSoonOnly ? (
          <div className="vcard-oos-overlay">Out of Stock</div>
        ) : (
          <div className="vcard-bottom-bar">
            <span className="vcard-unit-label">per {unit}</span>
            {/* The bar is kept rather than replaced, so "per kg" survives and
                every card in the row stays the same height. What changes is the
                control: a disabled button where ADD was, which is what a
                shopper already understands from everywhere else. */}
            {comingSoonOnly ? (
              <button className="vcard-add-btn vcard-add-btn--soon" disabled aria-disabled="true">
                SOON
              </button>
            ) : inCart === 0 ? (
              <button className="vcard-add-btn" onClick={handleAdd}>ADD</button>
            ) : (
              <div className="vcard-counter">
                <button className="vcard-counter-btn" onClick={handleDecrement}>−</button>
                <span className="vcard-counter-qty">{inCart}</span>
                <button
                  className="vcard-counter-btn"
                  onClick={handleIncrement}
                  disabled={inCart >= effectiveMax}
                >+</button>
              </div>
            )}
          </div>
        )}
      </div>

      {imgUrls.length > 1 && (
        <div className="vcard-dots">
          {imgUrls.map((_, i) => (
            <span
              key={i}
              className={`vcard-dot${i === imgIdx ? ' vcard-dot--active' : ''}`}
              onClick={() => setImgIdx(i)}
            />
          ))}
        </div>
      )}

      <Link to={`/vegetable/${id}`} className="vcard-info">
        <div className="vcard-price-row">
          <Text className="vcard-price">₹{price}</Text>
          <Text className="vcard-price-unit">/{unit}</Text>
          {showMrp && <Text className="vcard-mrp">₹{mrp}</Text>}
        </div>

        {/* One quiet line, not a third pill. The card already carries a
            seasonal tag and a size chip; a shouting badge beside them reads as
            clutter, and an offer that looks like clutter is an offer nobody
            finishes reading. */}
        {offer && <div className="vcard-offer">{offerText(offer)}</div>}

        {hasVariants && (
          <div className="vcard-variants-chip">
            {activeVariants.map(v => v.label).join(' • ')}
          </div>
        )}

        <div className="vcard-name">{name}</div>
        {nameHi && <div className="vcard-name-hi">{nameHi}</div>}
      </Link>
    </div>
  );
};

export default VegetableCard;
